import { StandardEvents } from '@shopify/events';

(() => {
  const REGISTRY_KEY = Symbol.for('vellacove.pod-blanket.lifecycle.v3');
  if (window[REGISTRY_KEY]) return;

  const registry = { instances: new Map(), intentByProduct: new Map(), observer: null, scanFrame: 0 };
  window[REGISTRY_KEY] = registry;

  const mediaIdForSlide = (slide) => {
    const token = [...slide.classList].find((name) => name.startsWith('vc-pod-media-id-'));
    return token ? token.slice('vc-pod-media-id-'.length) : '';
  };
  const setText = (element, text) => {
    if (element && element.textContent !== text) element.textContent = text;
  };
  const hasCrop = (frame) => frame && (frame.scale !== 1 || frame.x !== 0 || frame.y !== 0 || frame.fit !== 'cover');
  const getFrame = (data, mediaId) => {
    const raw = data.media_framing?.[String(mediaId)];
    if (!raw) return null;
    const frame = {
      scale: Number(raw.scale ?? 1),
      x: Number(raw.x ?? 0),
      y: Number(raw.y ?? 0),
      fit: raw.fit === 'contain' ? 'contain' : 'cover',
    };
    if (!Number.isFinite(frame.scale) || frame.scale <= 0 || !Number.isFinite(frame.x) || !Number.isFinite(frame.y)) return null;
    return frame;
  };
  const writeFrameVars = (element, frame) => {
    element.style.setProperty('--vc-pod-frame-scale', String(frame.scale));
    element.style.setProperty('--vc-pod-frame-x', `${frame.x}%`);
    element.style.setProperty('--vc-pod-frame-y', `${frame.y}%`);
    element.style.setProperty('--vc-pod-frame-size', `${frame.scale * 100}%`);
    element.style.setProperty('--vc-pod-frame-fit', frame.fit);
  };
  const unwrapThumbnail = (button) => {
    const frame = button.querySelector('.vc-pod-photo-frame');
    if (!frame) return;
    frame.before(frame.querySelector('img'));
    frame.remove();
    button.classList.remove('vc-pod-thumbnail-framed');
  };

  function disposeRoot(root) {
    const record = registry.instances.get(root);
    if (!record) return;
    record.dispose();
    registry.instances.delete(root);
  }

  function initialize(configElement) {
    const root = configElement.closest('.shopify-section');
    if (!root) return;
    const existing = registry.instances.get(root);
    let config;
    try {
      config = JSON.parse(configElement.textContent);
    } catch (error) {
      if (existing && existing.configElement !== configElement) disposeRoot(root);
      root.dataset.vcPodSchemaState = 'invalid-json';
      console.error('[Vellacove POD] Invalid local product config JSON.', error);
      registry.instances.set(root, { configElement, dispose: () => {} });
      return;
    }
    if (existing?.configElement === configElement) return;
    if (existing) disposeRoot(root);

    const data = config?.data;
    const allowedOptions = Array.isArray(config?.optionNames)
      && config.optionNames.includes('Size')
      && config.optionNames.every((name) => name === 'Size' || name === 'Thickness');
    const validV3 = data?.version === 3
      && Array.isArray(data.gallery_roles)
      && data.gallery_roles.some((role) => role.role === 'overview' && role.media_id != null)
      && data.gallery_roles.some((role) => role.role === 'selected_size' && role.source === 'selected_variant.featured_media')
      && Array.isArray(data.fabrics)
      && Array.isArray(config.variants)
      && allowedOptions;
    if (!validV3) {
      root.dataset.vcPodSchemaState = 'unsupported';
      console.warn('[Vellacove POD] Custom PDP enhancements are disabled for this product schema. Native Horizon product controls remain available.', {
        version: data?.version ?? null,
        optionNames: config?.optionNames ?? null,
      });
      registry.instances.set(root, { configElement, productId: config?.productId, dispose: () => {} });
      return;
    }

    delete root.dataset.vcPodSchemaState;
    root.classList.add('vc-pod-purchase');
    const lifecycle = new AbortController();
    let destroyed = false;
    let localObserver = null;
    let queued = false;
    let changeNumber = 0;
    let imageIntent = 0;
    let desiredSlide = 0;
    let desiredRole = 'overview';
    let pending = false;
    let currentVariant = null;
    let cover = null;
    let coverHost = null;
    let hostPosition = '';
    let coverFrame = 0;
    const previewCache = new Map();
    const frame = () => new Promise((resolve) => requestAnimationFrame(resolve));
    const listen = (target, name, callback, options = {}) => {
      target.addEventListener(name, callback, { ...options, signal: lifecycle.signal });
    };

    const releaseCover = () => {
      cancelAnimationFrame(coverFrame);
      coverFrame = 0;
      document.removeEventListener('scroll', alignCover, true);
      window.removeEventListener('resize', alignCover);
      cover?.remove();
      cover = null;
      root.classList.remove('vc-pod-gallery-transition');
      if (coverHost) {
        coverHost.style.position = hostPosition;
        coverHost = null;
      }
    };
    const alignCover = () => {
      if (!cover || !coverHost) return;
      const viewport = root.querySelector('media-gallery slideshow-slides');
      if (!viewport || !coverHost.isConnected) {
        releaseCover();
        return;
      }
      const bounds = viewport.getBoundingClientRect();
      const host = coverHost.getBoundingClientRect();
      Object.assign(cover.style, {
        left: `${bounds.left - host.left}px`,
        top: `${bounds.top - host.top}px`,
        width: `${bounds.width}px`,
        height: `${bounds.height}px`,
      });
    };
    const trackCover = () => {
      alignCover();
      if (cover && !destroyed) coverFrame = requestAnimationFrame(trackCover);
    };
    const holdPhoto = () => {
      if (cover) return;
      const gallery = root.querySelector('media-gallery');
      const viewport = gallery?.querySelector('slideshow-slides');
      if (!viewport) return;
      const bounds = viewport.getBoundingClientRect();
      const image = [...viewport.querySelectorAll('slideshow-slide img')].find((candidate) => {
        const rect = candidate.getBoundingClientRect();
        return rect.left < bounds.left + bounds.width / 2 && rect.right > bounds.left + bounds.width / 2;
      });
      if (!image) return;
      coverHost = gallery.parentElement;
      hostPosition = coverHost.style.position;
      if (getComputedStyle(coverHost).position === 'static') coverHost.style.position = 'relative';
      const host = coverHost.getBoundingClientRect();
      const photo = image.getBoundingClientRect();
      cover = document.createElement('div');
      cover.className = 'vc-pod-gallery-hold';
      cover.setAttribute('aria-hidden', 'true');
      Object.assign(cover.style, {
        position: 'absolute',
        left: `${bounds.left - host.left}px`,
        top: `${bounds.top - host.top}px`,
        width: `${bounds.width}px`,
        height: `${bounds.height}px`,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: '3',
        background: getComputedStyle(viewport).backgroundColor,
      });
      const copy = document.createElement('img');
      copy.src = image.currentSrc || image.src;
      copy.alt = '';
      Object.assign(copy.style, {
        position: 'absolute',
        left: `${(photo.left - bounds.left) / bounds.width * 100}%`,
        top: `${(photo.top - bounds.top) / bounds.height * 100}%`,
        width: `${photo.width / bounds.width * 100}%`,
        height: `${photo.height / bounds.height * 100}%`,
        maxWidth: 'none',
        objectFit: getComputedStyle(image).objectFit,
      });
      cover.append(copy);
      coverHost.append(cover);
      root.classList.add('vc-pod-gallery-transition');
      document.addEventListener('scroll', alignCover, { capture: true, passive: true });
      window.addEventListener('resize', alignCover, { passive: true });
      coverFrame = requestAnimationFrame(trackCover);
    };

    const optionFields = () => {
      const picker = root.querySelector('variant-picker');
      if (!picker) return new Map();
      const fields = new Map();
      picker.querySelectorAll('fieldset').forEach((fieldset) => {
        const legend = fieldset.querySelector('legend');
        const optionName = fieldset.dataset.vcPodOptionName || legend?.textContent.trim();
        if (!optionName || !config.optionNames.includes(optionName)) return;
        fieldset.dataset.vcPodOptionName = optionName;
        fields.set(optionName, fieldset);
      });
      return fields;
    };
    const selectionFromFields = (fields = optionFields()) => {
      const values = {};
      fields.forEach((fieldset, name) => {
        const checked = fieldset.querySelector('input:checked');
        if (checked) values[name] = checked.value;
      });
      return values;
    };
    const findVariant = (selection) => {
      if (!selection?.Size) return null;
      return config.variants.find((variant) => config.optionNames.every((name, index) => variant.options[index] === selection[name])) || null;
    };
    const selectedVariant = (fields = optionFields()) => findVariant(selectionFromFields(fields));
    const slides = () => [...root.querySelectorAll('media-gallery slideshow-slide')];
    const slideIndexForMedia = (mediaId) => slides().findIndex((slide) => mediaIdForSlide(slide) === String(mediaId));
    const sizeSlideIndex = (variant) => variant?.mediaId ? slideIndexForMedia(variant.mediaId) : -1;
    const sizeMediaIds = new Set(config.variants.map((variant) => String(variant.mediaId || '')).filter(Boolean));
    const overviewSlideIndex = () => {
      const role = data.gallery_roles.find((item) => item.role === 'overview');
      return role?.media_id ? slideIndexForMedia(role.media_id) : -1;
    };
    const resolveDesiredSlide = (variant) => {
      if (desiredRole === 'selected_size') return sizeSlideIndex(variant);
      if (desiredRole === 'overview') return overviewSlideIndex();
      return desiredSlide >= 0 && desiredSlide < slides().length ? desiredSlide : -1;
    };
    const selectedSizeRole = () => data.gallery_roles.find((role) => role.role === 'selected_size');
    const roleForMedia = (mediaId) => {
      if (mediaId && sizeMediaIds.has(String(mediaId))) return selectedSizeRole();
      return data.gallery_roles.find((role) => role.media_id != null && String(role.media_id) === String(mediaId)) || null;
    };
    const writeMainFrame = (slide, mediaId) => {
      const crop = getFrame(data, mediaId);
      const active = hasCrop(crop);
      slide.classList.toggle('vc-pod-framed-main', active);
      if (active) writeFrameVars(slide, crop);
      else ['--vc-pod-frame-scale', '--vc-pod-frame-x', '--vc-pod-frame-y', '--vc-pod-frame-size', '--vc-pod-frame-fit'].forEach((key) => slide.style.removeProperty(key));
    };
    const writeThumbnailFrame = (button, image, mediaId) => {
      const crop = getFrame(data, mediaId);
      if (!hasCrop(crop)) {
        unwrapThumbnail(button);
        return;
      }
      let frameElement = button.querySelector('.vc-pod-photo-frame');
      if (!frameElement) {
        frameElement = document.createElement('span');
        frameElement.className = 'vc-pod-photo-frame';
        image.before(frameElement);
        frameElement.append(image);
      }
      button.classList.add('vc-pod-thumbnail-framed');
      writeFrameVars(frameElement, crop);
    };
    const applyGalleryRolesAndFrames = (variant) => {
      const mediaSlides = slides();
      const renderedMediaIds = new Set(mediaSlides.map(mediaIdForSlide).filter(Boolean));
      const unresolvedRoles = data.gallery_roles.filter((role) => role.media_id != null && !renderedMediaIds.has(String(role.media_id)));
      const selectedMediaMissing = !pending && variant?.mediaId && !renderedMediaIds.has(String(variant.mediaId));
      if (unresolvedRoles.length || selectedMediaMissing) {
        root.dataset.vcPodMediaState = 'unresolved-role-or-variant-media';
        if (!root.dataset.vcPodMediaWarningLogged) {
          console.error('[Vellacove POD] A configured gallery role or selected variant media ID is missing from the rendered gallery.', {
            unresolvedRoles: unresolvedRoles.map((role) => role.role),
            selectedVariantMediaId: variant?.mediaId ?? null,
          });
          root.dataset.vcPodMediaWarningLogged = 'true';
        }
      } else {
        delete root.dataset.vcPodMediaState;
        delete root.dataset.vcPodMediaWarningLogged;
      }
      mediaSlides.forEach((slide) => writeMainFrame(slide, mediaIdForSlide(slide)));
      root.querySelectorAll('.slideshow-controls__thumbnails').forEach((list) => {
        [...list.querySelectorAll('.slideshow-controls__thumbnail')].forEach((button, index) => {
          const slide = mediaSlides[index];
          const mediaId = slide ? mediaIdForSlide(slide) : '';
          button.dataset.vcPodMediaId = mediaId;
          const mediaRole = roleForMedia(mediaId);
          const labelText = mediaRole?.label || '';
          button.classList.toggle('vc-pod-thumb-overview', mediaRole?.role === 'overview');
          let caption = button.querySelector('.vc-pod-thumb-title');
          if (labelText && !caption) {
            caption = document.createElement('span');
            caption.className = 'vc-pod-thumb-title';
            button.append(caption);
          }
          if (caption) {
            setText(caption, labelText);
            caption.hidden = !labelText;
          }
          const image = button.querySelector('img');
          if (image && mediaId) writeThumbnailFrame(button, image, mediaId);
        });
      });
    };
    const framePreview = (image, variant) => {
      const preview = image.closest('.vc-pod-size-preview');
      const crop = getFrame(data, variant?.mediaId);
      preview?.classList.toggle('vc-pod-framed', hasCrop(crop));
      if (preview && hasCrop(crop)) writeFrameVars(preview, crop);
      if (preview && !hasCrop(crop)) ['--vc-pod-frame-scale', '--vc-pod-frame-x', '--vc-pod-frame-y', '--vc-pod-frame-size', '--vc-pod-frame-fit'].forEach((key) => preview.style.removeProperty(key));
    };
    const showSlide = (index) => {
      if (index >= 0) root.querySelectorAll('.slideshow-controls__thumbnail')[index]?.click();
    };
    const preparePhoto = (src) => {
      if (!previewCache.has(src)) {
        const image = new Image();
        image.decoding = 'async';
        image.src = src;
        const ready = image.decode().then(() => image).catch(() => null);
        previewCache.set(src, { image, ready });
      }
      return previewCache.get(src);
    };
    const previewURL = (variant) => {
      try {
        const url = new URL(variant.previewImage, location.href);
        url.searchParams.set('width', matchMedia('(max-width:749px)').matches ? '832' : '1600');
        return url.href;
      } catch {
        return '';
      }
    };
    const setCoverImageGeometry = (image, mediaId) => {
      const crop = getFrame(data, mediaId);
      Object.assign(image.style, crop && hasCrop(crop) ? {
        left: `${crop.x}%`,
        top: `${crop.y}%`,
        width: `${crop.scale * 100}%`,
        height: `${crop.scale * 100}%`,
        objectFit: crop.fit,
      } : {
        left: '0', top: '0', width: '100%', height: '100%', objectFit: 'cover',
      });
    };
    const previewPhoto = (src, mediaId) => {
      if (!src) return;
      const version = changeNumber;
      const intent = ++imageIntent;
      holdPhoto();
      const cached = preparePhoto(src);
      if (cover) cover.dataset.requestedSource = src;
      const display = (image) => {
        if (!image || !cover || destroyed || version !== changeNumber || intent !== imageIntent) return;
        const copy = cover.querySelector('img');
        copy.src = image.src;
        setCoverImageGeometry(copy, mediaId);
        cover.dataset.previewSource = image.src;
      };
      if (cached.image.complete && cached.image.naturalWidth) display(cached.image);
      else cached.ready.then(display);
    };
    const previewSelectedSize = () => {
      const variant = selectedVariant();
      const src = previewURL(variant || {});
      if (variant?.previewImage) previewPhoto(src, variant.mediaId);
    };
    const preload = () => {
      for (const src of new Set(config.variants.map(previewURL).filter(Boolean))) preparePhoto(src);
    };
    const schedule = () => {
      if (queued || destroyed) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        if (!destroyed) update();
      });
    };

    function update() {
      const fields = optionFields();
      const sizeField = fields.get('Size');
      const thicknessField = fields.get('Thickness');
      if (!sizeField) {
        root.dataset.vcPodSchemaState = 'missing-size-option';
        console.error('[Vellacove POD] v3 product config requires a native Size option.');
        return;
      }
      sizeField.dataset.podSize = '1';
      sizeField.id = 'vc-pod-size-options';
      const sizeLegend = sizeField.querySelector('legend');
      setText(sizeLegend, fields.size > 1 ? '1  Choose your size' : 'Choose your size');
      if (thicknessField) {
        thicknessField.dataset.podWeight = '1';
        setText(thicknessField.querySelector('legend'), '2  Choose your thickness');
      }
      const variant = selectedVariant(fields);
      const size = selectionFromFields(fields).Size;
      const thickness = selectionFromFields(fields).Thickness;
      const fabric = thickness ? data.fabrics.find((item) => item.option === thickness) : data.fabrics.length === 1 ? data.fabrics[0] : null;
      if (!variant || !fabric) {
        root.dataset.vcPodSchemaState = 'invalid-option-mapping';
        console.error('[Vellacove POD] Variant options do not map one-to-one to configured fabrics.');
        return;
      }
      delete root.dataset.vcPodSchemaState;
      currentVariant = variant;
      if (thicknessField && data.fabrics.length > 1) {
        thicknessField.querySelectorAll('label').forEach((label) => {
          const input = label.querySelector('input');
          if (!input) return;
          const optionFabric = data.fabrics.find((item) => item.option === input.value);
          if (!optionFabric) return;
          let card = label.querySelector('.vc-pod-option-card');
          if (!card) {
            card = document.createElement('span');
            card.className = 'vc-pod-option-card';
            if (optionFabric.image) {
              const image = document.createElement('img');
              image.src = optionFabric.image;
              image.alt = '';
              image.width = 400;
              image.height = 400;
              card.append(image);
            }
            card.append(document.createElement('strong'), document.createElement('small'));
            label.append(card);
          }
          setText(card.querySelector('strong'), optionFabric.name);
          const optionVariant = findVariant({ Size: size, Thickness: input.value });
          setText(card.querySelector('small'), `${optionFabric.gsm} GSM · ${optionVariant?.price || ''}`);
        });
      }
      let preview = sizeField.querySelector('.vc-pod-size-preview');
      if (!preview) {
        preview = document.createElement('a');
        preview.className = 'vc-pod-size-preview';
        preview.href = '#MainContent';
        const image = document.createElement('img');
        image.width = 104;
        image.height = 104;
        image.alt = '';
        const text = document.createElement('span');
        text.textContent = 'View larger';
        preview.append(image, text);
        sizeLegend.after(preview);
      }
      if (variant.image) {
        const image = preview.querySelector('img');
        if (image.getAttribute('src') !== variant.image) image.src = variant.image;
        preview.setAttribute('aria-label', `View ${size} blanket larger`);
        framePreview(image, variant);
      }
      const weightLabel = thickness || `${fabric.gsm} GSM`;
      const summary = root.querySelector('#vc-pod-selection');
      setText(summary, `${size} · ${weightLabel} — ${variant.price}`);
      root.querySelectorAll('[data-pod-review]').forEach((element) => setText(element, `${size} · ${weightLabel} — ${variant.price}`));
      applyGalleryRolesAndFrames(variant);
    }

    const onSlideSelect = (event) => {
      if (!pending && event.detail?.userInitiated && event.target.closest('media-gallery')) {
        desiredSlide = event.detail.index;
        const slide = slides()[desiredSlide];
        const role = roleForMedia(mediaIdForSlide(slide));
        desiredRole = role?.role === 'selected_size' ? 'selected_size' : role?.role === 'overview' ? 'overview' : 'slide';
      }
    };
    const onThumbnailClick = (event) => {
      const button = event.target.closest('.slideshow-controls__thumbnail');
      if (!button) return;
      desiredSlide = [...root.querySelectorAll('.slideshow-controls__thumbnail')].indexOf(button);
      const mediaId = button.dataset.vcPodMediaId;
      const role = roleForMedia(mediaId);
      desiredRole = role?.role === 'selected_size' ? 'selected_size' : role?.role === 'overview' ? 'overview' : 'slide';
      imageIntent++;
      if (pending) {
        if (button.dataset.vcPodMediaId && button.dataset.vcPodMediaId === String(currentVariant?.mediaId)) previewSelectedSize();
        else {
          const slide = slides()[desiredSlide];
          const image = slide?.querySelector('img');
          if (image) previewPhoto(image.currentSrc || image.src, mediaIdForSlide(slide));
        }
      }
    };
    const onOptionClick = (event) => {
      const label = event.target.closest('variant-picker label');
      const input = event.target.matches('variant-picker input') ? event.target : label?.querySelector('input');
      const field = input?.closest('fieldset');
      if (!input || field?.dataset.vcPodOptionName !== 'Size') return;
      const selection = selectionFromFields();
      selection.Size = input.value;
      const intended = findVariant(selection);
      const index = sizeSlideIndex(intended);
      desiredRole = 'selected_size';
      if (index >= 0) desiredSlide = index;
      if (input.checked && !pending && index >= 0) showSlide(index);
    };
    const onOptionChange = (event) => {
      if (!event.target.matches('variant-picker input')) return;
      const field = event.target.closest('fieldset');
      const optionName = field?.dataset.vcPodOptionName;
      const wasViewingSize = desiredRole === 'selected_size';
      const nextVariant = selectedVariant();
      changeNumber++;
      if (optionName === 'Size' || wasViewingSize) {
        desiredRole = 'selected_size';
        const nextSizeIndex = sizeSlideIndex(nextVariant);
        if (nextSizeIndex >= 0) desiredSlide = nextSizeIndex;
      }
      pending = true;
      imageIntent++;
      if (desiredRole === 'selected_size' && nextVariant) previewSelectedSize();
      schedule();
    };
    const onProductSelect = (event) => {
      const version = changeNumber;
      holdPhoto();
      event.promise.then(async () => {
        await frame();
        await frame();
        if (destroyed || version !== changeNumber) return;
        const gallery = root.querySelector('media-gallery');
        const mediaSlides = [...(gallery?.querySelectorAll('slideshow-slide') || [])];
        const index = resolveDesiredSlide(selectedVariant());
        const image = mediaSlides[index]?.querySelector('img');
        if (image) {
          image.loading = 'eager';
          await image.decode().catch(() => {});
        }
        if (destroyed || version !== changeNumber) return;
        const finalIndex = resolveDesiredSlide(selectedVariant());
        if (finalIndex >= 0) {
          desiredSlide = finalIndex;
          await gallery?.querySelector('slideshow-component')?.select(finalIndex, undefined, { animate: false });
        }
        await frame();
        await frame();
        if (destroyed || version !== changeNumber) return;
        releaseCover();
        pending = false;
        schedule();
      }).catch(() => {
        if (!destroyed && version === changeNumber) {
          releaseCover();
          pending = false;
        }
      });
    };
    const onPreviewClick = (event) => {
      const preview = event.target.closest('.vc-pod-size-preview');
      if (!preview) return;
      event.preventDefault();
      const index = sizeSlideIndex(currentVariant);
      if (index >= 0) {
        desiredRole = 'selected_size';
        desiredSlide = index;
        showSlide(index);
      }
      root.querySelector('media-gallery')?.scrollIntoView({
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        block: 'start',
      });
    };

    listen(root, 'slideshow:select', onSlideSelect);
    listen(root, 'click', onThumbnailClick, { capture: true });
    listen(root, 'click', onOptionClick, { capture: true });
    listen(root, 'change', onOptionChange, { capture: true });
    listen(root, 'change', schedule);
    listen(root, StandardEvents.productSelect, onProductSelect, { capture: true });
    listen(root, 'click', onPreviewClick);

    localObserver = new MutationObserver(schedule);
    localObserver.observe(root, { childList: true, subtree: true });
    preload();
    update();
    const restoredIntent = config.productId ? registry.intentByProduct.get(String(config.productId)) : null;
    if (restoredIntent) {
      registry.intentByProduct.delete(String(config.productId));
      desiredRole = restoredIntent.role;
      const restoredIndex = restoredIntent.role === 'selected_size'
        ? sizeSlideIndex(currentVariant)
        : restoredIntent.role === 'overview'
          ? overviewSlideIndex()
          : slideIndexForMedia(restoredIntent.mediaId);
      if (restoredIndex >= 0) {
        desiredSlide = restoredIndex;
        requestAnimationFrame(() => {
          if (!destroyed) root.querySelector('media-gallery slideshow-component')?.select(restoredIndex, undefined, { animate: false });
        });
      }
    }

    const dispose = () => {
      if (destroyed) return;
      if (config.productId && (pending || desiredRole !== 'overview')) {
        const intentSlide = slides()[desiredSlide];
        registry.intentByProduct.set(String(config.productId), {
          role: desiredRole,
          mediaId: mediaIdForSlide(intentSlide),
        });
      }
      destroyed = true;
      lifecycle.abort();
      localObserver?.disconnect();
      releaseCover();
      root.classList.remove('vc-pod-purchase', 'vc-pod-gallery-transition');
    };
    registry.instances.set(root, { configElement, productId: config.productId, dispose });
  }

  const scan = () => {
    registry.scanFrame = 0;
    for (const root of registry.instances.keys()) {
      if (!root.isConnected) disposeRoot(root);
    }
    document.querySelectorAll('#vc-pod-config').forEach(initialize);
  };
  const scheduleScan = () => {
    if (!registry.scanFrame) registry.scanFrame = requestAnimationFrame(scan);
  };
  const handleSectionUnload = (event) => {
    for (const root of registry.instances.keys()) {
      if (event.target === root || event.target.contains?.(root)) disposeRoot(root);
    }
  };
  document.addEventListener('shopify:section:load', scheduleScan);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
  document.addEventListener('DOMContentLoaded', scheduleScan, { once: true });
  registry.observer = new MutationObserver(scheduleScan);
  registry.observer.observe(document.documentElement, { childList: true, subtree: true });
  scan();
})();
